import { type ComponentProps, useEffect, useRef, useState } from "react";

import { DEFAULT_SUBSCRIPTION_PROCESSING_TIMEOUT_MS } from "@/domains/subscriptions/models/constants";

type PieCountdownProps = Pick<ComponentProps<"svg">, "className"> & {
  startMs: number;
  totalDurationMs: number;
};

const SIZE = 100;
const RADIUS = SIZE / 2 - 5;
const CENTER = SIZE / 2;

const polarToCartesian = (cx: number, cy: number, r: number, angleDegrees: number) => {
  const angleRadians = (angleDegrees * Math.PI) / 180;

  return {
    x: cx + r * Math.cos(angleRadians),
    y: cy + r * Math.sin(angleRadians),
  };
};

const getProgress = (currentMs: number, totalMs: number) => {
  return Math.max(0, Math.min((Date.now() - currentMs) / totalMs, 1));
};

const createPieSlicePath = (progress: number) => {
  if (progress <= 0) {
    return "";
  }

  const startAngle = -90;
  const endAngle = startAngle + progress * 360;
  const start = polarToCartesian(CENTER, CENTER, RADIUS, startAngle);
  const end = polarToCartesian(CENTER, CENTER, RADIUS, endAngle);
  const largeArcFlag = progress > 0.5 ? 1 : 0;

  return [
    `M ${CENTER} ${CENTER}`,
    `L ${start.x} ${start.y}`,
    `A ${RADIUS} ${RADIUS} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
};

export const PieCountdown = ({
  className = "size-12",
  startMs,
  totalDurationMs,
}: PieCountdownProps) => {
  const safeDurationMs =
    Number.isFinite(totalDurationMs) && totalDurationMs > 0
      ? totalDurationMs
      : DEFAULT_SUBSCRIPTION_PROCESSING_TIMEOUT_MS;

  const [progress, setProgress] = useState(() => {
    return getProgress(startMs, safeDurationMs);
  });
  const animationFrameRef = useRef(0);

  useEffect(() => {
    const animate = () => {
      const newProgress = getProgress(startMs, safeDurationMs);
      setProgress(newProgress);

      if (newProgress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [startMs, safeDurationMs]);

  const pieSlicePath = createPieSlicePath(progress);

  return (
    <svg aria-hidden="true" className={className} viewBox={`0 0 ${SIZE} ${SIZE}`}>
      <circle
        className="text-content-subtle"
        cx={CENTER}
        cy={CENTER}
        fill="currentColor"
        r={RADIUS}
      />
      {progress >= 1 && (
        <circle className="text-content" cx={CENTER} cy={CENTER} fill="currentColor" r={RADIUS} />
      )}
      {progress < 1 && pieSlicePath ? (
        <path className="text-content" d={pieSlicePath} fill="currentColor" />
      ) : null}
    </svg>
  );
};
